import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { ROUTES } from 'routes'
import { UserManagementFormContext } from '../../../../contexts/UserManagementFormContext'
import { EditSchema } from '../../schemas/edit'
import { Data, UPDATE_EMPLOYEE, Variables } from './api'
import { mapper } from './mapper'
import { UseUpdateEmployee } from './types'

export const useUpdateEmployee = (): UseUpdateEmployee => {
  const navigate = useNavigate()
  const { t } = useTranslation('features/user')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(UserManagementFormContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_EMPLOYEE,
    onCompleted: async (data: Data) => {
      const {
        updateEmployee: { errors, user },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.employee.edit.error'), { variant: 'error' })
      } else if (user) {
        setErrors({})
        navigate(generatePath(ROUTES.USER.LIST, { tab: 'employees' }))
        enqueueSnackbar(t('form.employee.edit.success', { email: user.email }), {
          variant: 'success',
        })
      }
    },
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const updateEmployee = (data: EditSchema): void => {
    void mutateAsync({
      input: mapper(data),
    })
  }

  return {
    errors,
    updateEmployee,
  }
}
